import { useCallback, useEffect, useState } from 'react'

/** Minimal hash-based routing. The SPA has a handful of screens (chat + admin
 * + the stats dashboard), so a full router dependency isn't warranted — the
 * hash survives refresh and the back button, which is all we need for the
 * admin-gated entry points. */
export type RoutePath = 'chat' | 'admin' | 'stats'

const HASH_BY_PATH: Record<Exclude<RoutePath, 'chat'>, string> = {
  admin: '#/admin',
  stats: '#/stats',
}

function pathFromHash(hash: string): RoutePath {
  if (hash === HASH_BY_PATH.admin) return 'admin'
  if (hash === HASH_BY_PATH.stats) return 'stats'
  return 'chat'
}

export type UseHashRouteResult = {
  path: RoutePath
  navigate: (path: RoutePath) => void
}

export function useHashRoute(): UseHashRouteResult {
  const [hash, setHash] = useState<string>(() => window.location.hash)

  useEffect(() => {
    const onHashChange = (): void => setHash(window.location.hash)
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const navigate = useCallback((path: RoutePath) => {
    window.location.hash = path === 'chat' ? '' : HASH_BY_PATH[path]
  }, [])

  return { path: pathFromHash(hash), navigate }
}
